<script lang="ts">
export interface TableView<T extends string> {
  id: T
  icon: string
  label: string
  analyticsLabel: string
  checked: boolean
}
</script>

<script setup generic="T extends string" lang="ts">
import {
  type FilterItem,
  type FindManyFilterGroup,
  type PageFilter,
} from '@rialtic/types'
import { formatCount } from '#imports'

interface FilterChangeArg {
  item: FilterItem & { active: boolean }
  name: string
}

const search = defineModel<string>('search')

interface DataToolbarProps {
  analyticsTagMap: Record<string, string>
  collapsible?: boolean
  count: number
  filters?: PageFilter[]
  filtersWithCodeFormat?: string[]
  ignoredFilters?: string[]
  initialFilters?: string[]
  plurals: { one: string; other: string }
  searchableFilters?: string[]
  searchPlaceholder?: string
  searchAnalyticsTag?: string
  views?: TableView<T>[]
}
const {
  collapsible = false,
  filters = [],
  filtersWithCodeFormat = [],
  ignoredFilters,
  initialFilters = [],
  searchableFilters = [],
  searchPlaceholder = 'Search',
  views,
} = defineProps<DataToolbarProps>()

const emit = defineEmits<{
  'filter-all': [filterName: string]
  'filter-change': [change: FilterChangeArg]
  'filter-clear': [filterName: string]
  'toggle-view': [viewId: T]
}>()

const route = useRoute()
const { normalizedParams, deleteUrlParam } = useUrlParamsState()
const userSelectedFilters = useLocalStorage(
  `@rialtic/${String(route.name)}-filters`,
  () =>
    new Set<string>([
      ...initialFilters,
      ...Object.keys(normalizedParams.value),
    ]),
)
const visibleFilterCategories = computed(() =>
  filters
    ?.filter((filter) => userSelectedFilters.value.has(filter.name))
    .map((filter) => {
      const filterParams = normalizedParams.value[filter.name]

      return {
        ...filter,
        type: filter.type as keyof typeof filterComponents,
        items:
          filter.items?.map((item) => {
            const active =
              Array.isArray(filterParams) && filterParams.includes(item.value)
            return {
              ...item,
              active,
            }
          }) ?? [],
      }
    }),
)
const collapse = defineModel<boolean>('collapse')
const appliedFilterCount = computed(() =>
  visibleFilterCategories.value.reduce(
    (count, filter) =>
      normalizedParams.value[filter.name] ? count + 1 : count,
    0,
  ),
)

const showFilters = ({ item }: { item: FilterItem }) => {
  if (userSelectedFilters.value.has(item.value)) {
    userSelectedFilters.value.delete(item.value)
    deleteUrlParam(item.value)
    return
  }

  userSelectedFilters.value.add(item.value)
}

const { filterComponents, selectAllFiltersList } =
  useFiltersState(ignoredFilters)

const availableFilters = computed<PageFilter>(() => ({
  isMultiple: true,
  count: 8,
  items: filters.map((filterItem) => {
    return {
      active: userSelectedFilters.value.has(filterItem.name),
      label: filterItem.label,
      value: filterItem.name,
      group: (filterItem as FindManyFilterGroup).group,
    }
  }),
  label: 'Filters',
  name: 'Filters',
  type: 'multiple',
}))
</script>

<template>
  <header
    class="bg-surface z-1 sticky top-0 flex items-end border-b border-neutral-200 px-4 py-3 lg:px-8"
  >
    <div class="flex flex-1 flex-wrap items-center gap-2 pr-4">
      <UiMenu
        v-if="views?.length"
        class="relative flex"
        :menu-styles="{ maxWidth: 200 }"
        :placement="'bottom-start'"
        menu-classes="w-full"
      >
        <template #activator="{ toggle }">
          <button
            ref="button"
            class="w-full rounded-lg border border-neutral-200 px-3 py-2"
            aria-haspopup="menu"
            type="button"
            data-analytics="table_options_button"
            @click="toggle(true)"
          >
            <div
              class="flex items-center space-x-2 whitespace-nowrap text-xs font-medium text-neutral-800"
            >
              <div class="i-ph-columns text-primary-400 h-4.5 w-4.5" />
              <span>Table options</span>
              <div class="i-ph-caret-down-bold h-4 w-4" />
            </div>
          </button>
        </template>
        <div class="px-2 pb-2 pt-3 text-xs font-medium">Column Visibility</div>
        <ul class="flex flex-col p-0 text-neutral-900">
          <li v-for="view in views" :key="view.id">
            <UiListItemCheckbox
              :checked="view.checked"
              :data-analytics="view.analyticsLabel"
              @click="emit('toggle-view', view.id)"
            >
              <div class="align-items flex gap-1">
                <div class="text-primary-400 h-4.5 w-4.5" :class="view.icon" />
                {{ view.label }}
              </div>
            </UiListItemCheckbox>
          </li>
        </ul>
      </UiMenu>
      <UiSearch
        v-model="search"
        :initial-collapsed="true"
        :loading="false"
        :placeholder="searchPlaceholder"
        collapsible
        :data-analytics="searchAnalyticsTag"
      />
      <UiButton
        v-if="collapsible"
        :class="{
          'bg-secondary-50 text-secondary-800 border-transparent':
            appliedFilterCount > 0,
        }"
        size="xs"
        variant="outlined"
        data-analytics="collapse_filters_toggle"
        @click="collapse = !collapse"
      >
        <div class="flex items-center gap-2">
          <i
            class="size-4.5 block"
            :class="collapse ? 'i-ph-funnel-simple' : 'i-ph-funnel-simple-x'"
          />
          <span v-if="collapse">
            {{ appliedFilterCount }}
            <UiPluralize
              :count="appliedFilterCount"
              plural="filters"
              singular="filter"
            />
            applied
          </span>
          <span v-else>Hide Filters</span>
          <i v-if="collapse" class="i-ph-caret-down size-4.5 block" />
        </div>
      </UiButton>
      <template v-if="!collapse">
        <UiFilterDropDown
          v-if="availableFilters.items?.length"
          :clear-all-disabled="true"
          :filter="availableFilters"
          :select-all-disabled="true"
          data-analytics="filters-selector"
          data-testid="filters-selector"
          @filter-change="showFilters"
        >
          <template #activator="{ setOpen }">
            <UiFilterChip
              class="h-full"
              :label="availableFilters.label"
              button-border-radius="rounded-lg"
              @click="setOpen(true)"
            >
              <template #icon>
                <div class="i-ph-funnel-simple mx-auto mr-2 h-4 w-4" />
              </template>
            </UiFilterChip>
          </template>
        </UiFilterDropDown>
        <template v-for="filter in visibleFilterCategories">
          <slot v-bind="{ filter }" :name="`filter.${filter.name}`">
            <component
              :is="filterComponents[filter.type ?? 'single']"
              :key="filter.name"
              class="whitespace-nowrap"
              :filter="filter"
              :filters-with-code-format="filtersWithCodeFormat"
              :search="searchableFilters.includes(filter.name)"
              :select-all-disabled="!selectAllFiltersList.includes(filter.name)"
              :data-analytics="analyticsTagMap[filter.name]"
              :data-testid="
                'filter-' +
                filter.label.toLowerCase().replaceAll(' ', '-').split('(')[0]
              "
              @filter-all="(name: string) => emit('filter-all', name)"
              @filter-change="
                (filter: FilterChangeArg) => emit('filter-change', filter)
              "
              @filter-clear="(name: string) => emit('filter-clear', name)"
            />
          </slot>
        </template>
      </template>
    </div>
    <div v-if="!collapse" class="flex max-h-[38px] items-center space-x-2">
      <div class="flex items-center space-x-1 text-xs text-neutral-900">
        <div class="font-semibold" data-testid="data-grid-toolbar-count">
          {{ formatCount(count) }}
        </div>
        <div v-if="count === 1">{{ plurals.one }}</div>
        <div v-else>{{ plurals.other }}</div>
      </div>

      <slot name="actions" />
    </div>
  </header>
</template>
